import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormikTextField from 'ui/elements/form/formik/FormikTextField';
import Label from 'ui/elements/form/Label';
import TextField from 'ui/elements/form/TextField';
import Select from 'ui/elements/form/Select/Select';
import MultiSelect from 'ui/elements/form/Select/MultiSelect';
import Switch from 'ui/elements/form/choice/Switch';
import Checkbox from 'ui/elements/form/choice/Checkbox';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import ImageUploader from 'domain/shared/ImageUploader';

import {
  PartnerOfferType,
  PartnerOfferLayout,
  CreatePartnerOfferDTO,
  UpdatePartnerOfferDTO,
  BackofficePartnerOffer,
  PartnerOffer,
} from 'domain/partnerOffers/types';
import { SimpleLabel } from 'types/company/company';
import { EditLabels } from './EditLabels';
import DatePicker from 'ui/elements/form/DatePicker';
import { contentSpacing, quarterSpacing } from 'ui/theme/themeConstants';
import { asIsoDateOnly } from 'util/dateUtils';
import SelectedPartnerOffer from 'pages/Discover/SelectedPartnerOffers/SelectedPartnerOffer';
import { bluePlanetTheme } from 'ui/theme';
import LabelEditor from './LabelEditor';
import Chip, { RoundedChip } from 'ui/elements/Chip';
import { contrastColorRGB, convertHexToRGB } from 'util/color';
import backofficeApi, { backofficeApiUrls } from 'apis/BackofficeAPI';
import InfoMessage from 'ui/views/messages/InfoMessage';
import Logo from 'ui/domain/Logo';
import { useBackofficeCommunities, BackofficeCommunity } from 'pages/Backoffice/useBackofficeCommunities';
import useResource from 'util/resource/useResource';
import { TinyOrganizationDTO } from 'types/organization';
import { getOrUndefined } from 'util/resource';
import fuzzysearch from 'fuzzysearch';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { BackofficeOrganizationDTO } from 'types/backoffice';
import { useBackofficePartnerOffers } from 'domain/partnerOffers';
import { useCompanies } from 'apis/CompanyAPI/companies/useCompanies';
import { CompanyListItem } from 'types/company';
import { useBackofficeOrganizations } from 'pages/Organization/useBackofficeOrganizations';

// Template variables that can be used in partner offer URLs
export const PARTNER_OFFER_TEMPLATE_VARIABLES = {
  NAME: '{name}',
  POSITION: '{position}',
  EMPLOYER: '{employer}',
  PHONE: '{phone}',
  EMAIL: '{email}',
  LOCATION: '{location}',
} as const;

// Helper function to get formatted helper text for forms
export const getTemplateVariablesHelperText = (): string => {
  const variables = Object.entries(PARTNER_OFFER_TEMPLATE_VARIABLES)
    .map(([, description]) => `${description}`)
    .join(', ');

  return `Use {braces} to insert variables into the url: ${variables}`;
};

const MainContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarContainer = styled.div`
  position: sticky;
  top: 2rem;
  height: fit-content;

  @media (max-width: 1200px) {
    position: static;
    order: -1;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: ${contentSpacing};

  ${bluePlanetTheme.breakpoints.down('sm')} {
    flex-direction: column;
    gap: 1rem;
  }
`;

const FormSection = styled.div`
  h3,
  h4 {
    margin: 0 0 1rem 0;
  }
  display: flex;
  flex-direction: column;
  gap: ${contentSpacing};
`;

interface PartnerOfferFormProps {
  offer?: BackofficePartnerOffer;
  onSubmit: (data: CreatePartnerOfferDTO | UpdatePartnerOfferDTO) => Promise<void>;
  isLoading?: boolean;
}

const validationSchema = Yup.object().shape({
  type: Yup.string(),
  rowNumber: Yup.number().required('Row is required'),
  linkText: Yup.string().nullable(),
  linkUrl: Yup.string()
    .test('url-or-template', 'Must be a valid URL or URL template', value => {
      if (!value) return true; // Allow empty values
      // Allow URLs with template variables like {variable}
      const urlWithTemplateRegex = /^(https?:\/\/)?[^\s]+$/i;
      return urlWithTemplateRegex.test(value);
    })
    .nullable(),
  description: Yup.string().nullable(),
  bannerLabelColor: Yup.string()
    .matches(/^#[0-9A-F]{6}$/i, 'Must be a valid hex color')
    .nullable(),
});

export default function PartnerOfferForm({ offer, onSubmit, isLoading }: PartnerOfferFormProps) {
  const [labels, setLabels] = useState<SimpleLabel[]>(offer?.labels || []);
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [communitySearch, setCommunitySearch] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const { resource: organizationResource } = useBackofficeOrganizations({ limit: 13337 });
  const { resource: communityResource } = useBackofficeCommunities();
  const { resource: companiesResource } = useCompanies();
  const { resource: partnerOffersGroupedResource } = useBackofficePartnerOffers();

  const availableRows =
    getOrUndefined(partnerOffersGroupedResource)?.map(row => ({
      value: row.rowNumber,
      label: `Row ${row.rowNumber}: ${row.rowName}`,
    })) || [];

  const formik = useFormik({
    initialValues: {
      type: offer?.type || PartnerOfferType.Custom,
      layout: offer?.layout || PartnerOfferLayout.Default,
      rowNumber: offer?.rowNumber,
      name: offer?.name,
      logoUrl: offer?.logoUrl,
      bannerUrl: offer?.bannerUrl,
      bannerLabelText: offer?.bannerLabel?.name,
      bannerLabelColor: offer?.bannerLabel?.color || bluePlanetTheme.bluePlanetPalette.purple.main,
      description: offer?.description,
      linkText: offer?.linkText,
      linkUrl: offer?.linkUrl,
      isVisible: offer?.isVisible,
      isVisibleOnHome: offer?.isVisibleOnHome,
      expirationDate: offer?.expirationDate,
      audienceCountries: offer?.audienceCountries || [],
      organizationId: offer?.organizationId || '',
      communityId: offer?.communityId || null,
      companyId: offer?.companyId || null,
    },
    validationSchema,
    onSubmit: async values => {
      const submitData = {
        ...values,
        labels,
        expirationDate: values.expirationDate || undefined,
        // Clear irrelevant IDs based on type
        organizationId: values.type === PartnerOfferType.Organization ? values.organizationId : undefined,
        communityId: values.type === PartnerOfferType.Community ? values.communityId : undefined,
        companyId: values.type === PartnerOfferType.Company ? values.companyId : undefined,
      };

      if (offer?.id) {
        await onSubmit({ id: offer.id, ...submitData } as UpdatePartnerOfferDTO);
      } else {
        await onSubmit(submitData as CreatePartnerOfferDTO);
      }
    },
  });

  const typeOptions = [
    { value: PartnerOfferType.Custom, label: 'Custom' },
    { value: PartnerOfferType.Community, label: 'Community' },
    { value: PartnerOfferType.Organization, label: 'Organization' },
    { value: PartnerOfferType.Company, label: 'Company' },
  ];

  const layoutOptions = [
    { value: PartnerOfferLayout.Default, label: 'Default' },
    { value: PartnerOfferLayout.Simple, label: 'Simple' },
  ];

  // Country options for partner offer targeting
  const countryOptions = [
    // Top 5 most relevant markets
    { value: 'NO', label: 'Norway' },
    { value: 'SE', label: 'Sweden' },
    { value: 'DK', label: 'Denmark' },
    { value: 'FI', label: 'Finland' },
    { value: 'DE', label: 'Germany' },
    { value: 'GB', label: 'United Kingdom' },
    { value: 'US', label: 'United States' },

    // All other countries (alphabetically)
    { value: 'AR', label: 'Argentina' },
    { value: 'AU', label: 'Australia' },
    { value: 'AT', label: 'Austria' },
    { value: 'BE', label: 'Belgium' },
    { value: 'BR', label: 'Brazil' },
    { value: 'BG', label: 'Bulgaria' },
    { value: 'CA', label: 'Canada' },
    { value: 'CL', label: 'Chile' },
    { value: 'CN', label: 'China' },
    { value: 'CO', label: 'Colombia' },
    { value: 'HR', label: 'Croatia' },
    { value: 'CY', label: 'Cyprus' },
    { value: 'CZ', label: 'Czech Republic' },
    { value: 'EG', label: 'Egypt' },
    { value: 'EE', label: 'Estonia' },
    { value: 'FR', label: 'France' },
    { value: 'GR', label: 'Greece' },
    { value: 'HK', label: 'Hong Kong' },
    { value: 'HU', label: 'Hungary' },
    { value: 'IN', label: 'India' },
    { value: 'ID', label: 'Indonesia' },
    { value: 'IE', label: 'Ireland' },
    { value: 'IL', label: 'Israel' },
    { value: 'IS', label: 'Iceland' },
    { value: 'IT', label: 'Italy' },
    { value: 'JP', label: 'Japan' },
    { value: 'KE', label: 'Kenya' },
    { value: 'KR', label: 'South Korea' },
    { value: 'LV', label: 'Latvia' },
    { value: 'LT', label: 'Lithuania' },
    { value: 'LU', label: 'Luxembourg' },
    { value: 'MY', label: 'Malaysia' },
    { value: 'MT', label: 'Malta' },
    { value: 'MX', label: 'Mexico' },
    { value: 'NL', label: 'Netherlands' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'NG', label: 'Nigeria' },
    { value: 'PE', label: 'Peru' },
    { value: 'PH', label: 'Philippines' },
    { value: 'PL', label: 'Poland' },
    { value: 'PT', label: 'Portugal' },
    { value: 'RO', label: 'Romania' },
    { value: 'RU', label: 'Russia' },
    { value: 'SA', label: 'Saudi Arabia' },
    { value: 'SG', label: 'Singapore' },
    { value: 'SK', label: 'Slovakia' },
    { value: 'SI', label: 'Slovenia' },
    { value: 'ZA', label: 'South Africa' },
    { value: 'ES', label: 'Spain' },
    { value: 'CH', label: 'Switzerland' },
    { value: 'TW', label: 'Taiwan' },
    { value: 'TH', label: 'Thailand' },
    { value: 'TR', label: 'Turkey' },
    { value: 'UA', label: 'Ukraine' },
    { value: 'AE', label: 'United Arab Emirates' },
    { value: 'UY', label: 'Uruguay' },
    { value: 'VN', label: 'Vietnam' },
  ];

  const isCustomType = formik.values.type === PartnerOfferType.Custom;
  const isOrganizationType = formik.values.type === PartnerOfferType.Organization;
  const isCommunityType = formik.values.type === PartnerOfferType.Community;
  const isCompanyType = formik.values.type === PartnerOfferType.Company;

  // Get organizations, communities, and companies data
  const allOrganizations = getOrUndefined(organizationResource)?.values.map(org => org.details) || [];
  const allCommunities = getOrUndefined(communityResource)?.values || [];
  const allCompanies = getOrUndefined(companiesResource)?.values || [];

  // Filter organizations, communities, and companies based on search
  const filteredOrganizations = !organizationSearch
    ? allOrganizations
    : allOrganizations.filter(org => fuzzysearch(organizationSearch.toLowerCase(), org.name.toLowerCase()));

  const filteredCommunities = !communitySearch
    ? allCommunities
    : allCommunities.filter(community =>
        fuzzysearch(communitySearch.toLowerCase(), community.details.name.toLowerCase()),
      );

  const filteredCompanies = !companySearch
    ? allCompanies
    : allCompanies.filter(company => fuzzysearch(companySearch.toLowerCase(), company.name.toLowerCase()));

  // Get selected organization, community, and company for display
  const { resource: selectedOrganizationResource } = useResource<BackofficeOrganizationDTO>(
    formik.values.organizationId ? backofficeApiUrls.organizations.get(formik.values.organizationId) : undefined,
  );
  const selectedOrganization = getOrUndefined(selectedOrganizationResource);
  const selectedCommunity = allCommunities.find(community => community.details.id === formik.values.communityId);
  const selectedCompany = allCompanies.find(company => company.id === formik.values.companyId);

  // Preselect first available row for new offers
  useEffect(() => {
    if (!offer && availableRows.length > 0 && !formik.values.rowNumber) {
      formik.setFieldValue('rowNumber', availableRows[0].value);
    }
  }, [availableRows, offer, formik]);

  // Clear organization/community/company selection and search when type changes
  useEffect(() => {
    if (formik.values.type !== PartnerOfferType.Organization) {
      formik.setFieldValue('organizationId', '');
      setOrganizationSearch('');
    }
    if (formik.values.type !== PartnerOfferType.Community) {
      formik.setFieldValue('communityId', null);
      setCommunitySearch('');
    }
    if (formik.values.type !== PartnerOfferType.Company) {
      formik.setFieldValue('companyId', null);
      setCompanySearch('');
    }
  }, [formik.values.type, formik.setFieldValue]);

  const previewOffer: PartnerOffer = {
    name:
      (formik.values.name || selectedCommunity?.details.name || selectedCompany?.name) ??
      selectedOrganization?.name ??
      '',
    logoUrl:
      (formik.values.logoUrl || selectedCommunity?.details.logoURL || selectedCompany?.logoURL) ??
      selectedOrganization?.logoUrl,
    bannerUrl: (formik.values.bannerUrl || selectedCommunity?.imageURL) ?? selectedOrganization?.bannerUrl,
    bannerLabel: formik.values.bannerLabelText
      ? {
          name: formik.values.bannerLabelText,
          color: formik.values.bannerLabelColor || bluePlanetTheme.bluePlanetPalette.purple.main,
        }
      : undefined,
    description: formik.values.description || selectedCommunity?.description || selectedOrganization?.description,
    labels: (
      selectedOrganization?.types.map(industry => ({
        name: industry.name,
        color: bluePlanetTheme.bluePlanetPalette.grey.medium,
      })) || []
    ).concat(
      labels.map((label, index) => ({
        id: `temp-${index}`,
        name: label.name,
        color: label.color,
      })),
    ),
    linkText: formik.values.linkText,
    linkUrl: formik.values.linkUrl,
    type: formik.values.type,
    layout: formik.values.layout,
    organizationSlug: selectedOrganization?.slug,
    communitySlug: selectedCommunity?.details.slug,
  };

  const { isMobile } = useBreakpoints();

  return (
    <MainContainer>
      <form onSubmit={formik.handleSubmit}>
        <FormGrid>
          {/* Basic Information */}
          <FormSection>
            <FormRow style={{ alignItems: 'end' }}>
              <div style={{ minWidth: '200px' }}>
                <Switch
                  label="Visible"
                  value={formik.values.isVisible}
                  onChange={() => formik.setFieldValue('isVisible', !formik.values.isVisible)}
                />
              </div>
              <DatePicker
                selected={formik.values.expirationDate ? new Date(formik.values.expirationDate) : null}
                onChange={(date: Date | null) => formik.setFieldValue('expirationDate', asIsoDateOnly(date))}
                dateFormat="yyyy-MM-dd"
                placeholderText="Select expiration date"
                customInput={<TextField label="Expiration Date" />}
              />
              <div style={{ minWidth: '200px' }}>
                <Checkbox
                  label="Visible on Home"
                  checked={formik.values.isVisibleOnHome || false}
                  onChange={event => formik.setFieldValue('isVisibleOnHome', event.target.checked)}
                />
              </div>
            </FormRow>
            <FormRow>
              <div style={{ width: '200px' }}>
                <Label>Offer Type</Label>
                <Select
                  value={typeOptions.find(option => option.value === formik.values.type)}
                  onChange={option => formik.setFieldValue('type', option?.value)}
                  options={typeOptions}
                  getOptionLabel={option => option.label}
                  error={formik.touched.type ? !!formik.errors.type : false}
                  helperText={formik.touched.type ? formik.errors.type : undefined}
                />
              </div>
              <div style={{ width: '200px' }}>
                <Label>Layout</Label>
                <Select
                  value={layoutOptions.find(option => option.value === formik.values.layout)}
                  onChange={option => formik.setFieldValue('layout', option?.value)}
                  options={layoutOptions}
                  getOptionLabel={option => option.label}
                  error={formik.touched.layout ? !!formik.errors.layout : false}
                  helperText={formik.touched.layout ? formik.errors.layout : undefined}
                />
              </div>
              <div style={{ width: '200px' }}>
                <Label>Row Number *</Label>
                <Select
                  value={availableRows.find(option => option.value === formik.values.rowNumber)}
                  onChange={option => formik.setFieldValue('rowNumber', option?.value)}
                  options={availableRows}
                  getOptionLabel={option => option.label}
                  error={formik.touched.rowNumber ? !!formik.errors.rowNumber : false}
                  helperText={formik.touched.rowNumber ? formik.errors.rowNumber : undefined}
                />
              </div>
              {/* Organization Picker */}
              {isOrganizationType && (
                <div style={!isMobile ? { minWidth: '300px' } : {}}>
                  <Label>Select Organization</Label>
                  <Select
                    value={selectedOrganization}
                    onChange={(option: TinyOrganizationDTO | undefined) => {
                      formik.setFieldValue('organizationId', option?.id || '');
                    }}
                    onClear={() => formik.setFieldValue('organizationId', undefined)}
                    options={filteredOrganizations}
                    getOptionLabel={(option: TinyOrganizationDTO) => option.name}
                    renderOption={(props, option: TinyOrganizationDTO) => (
                      <li {...props} style={{ display: 'flex', alignItems: 'center', gap: quarterSpacing }}>
                        <Logo url={option.logoURL} size={30} type="organization" />
                        {option.name}
                      </li>
                    )}
                    placeholder="Search organizations..."
                    onInputChange={searchValue => setOrganizationSearch(searchValue)}
                    isClearable
                    getOptionKey={(option: TinyOrganizationDTO) => option.id.toString()}
                  />
                </div>
              )}

              {/* Community Picker */}
              {isCommunityType && (
                <div style={!isMobile ? { minWidth: '300px' } : {}}>
                  <Label>Select Community</Label>
                  <Select
                    value={selectedCommunity}
                    onChange={(option: BackofficeCommunity | undefined) => {
                      formik.setFieldValue('communityId', option?.details.id);
                    }}
                    onClear={() => formik.setFieldValue('communityId', undefined)}
                    options={filteredCommunities}
                    getOptionLabel={(option: BackofficeCommunity) => option.details.name}
                    renderOption={(props, option: BackofficeCommunity) => (
                      <li {...props} style={{ display: 'flex', alignItems: 'center', gap: quarterSpacing }}>
                        <Logo url={option.details.logoURL} size={30} type="community" />
                        {option.details.name}
                      </li>
                    )}
                    placeholder="Search communities..."
                    onInputChange={searchValue => setCommunitySearch(searchValue)}
                    isClearable
                    getOptionKey={(option: BackofficeCommunity) => option.details.id.toString()}
                  />
                </div>
              )}

              {/* Company Picker */}
              {isCompanyType && (
                <div style={!isMobile ? { minWidth: '300px' } : {}}>
                  <Label>Select Company</Label>
                  <Select
                    value={selectedCompany}
                    onChange={(option: CompanyListItem | undefined) => {
                      formik.setFieldValue('companyId', option?.id);
                    }}
                    onClear={() => formik.setFieldValue('companyId', undefined)}
                    options={filteredCompanies}
                    getOptionLabel={(option: CompanyListItem) => option.name}
                    renderOption={(props, option: CompanyListItem) => (
                      <li {...props} style={{ display: 'flex', alignItems: 'center', gap: quarterSpacing }}>
                        <Logo url={option.logoURL} size={30} type="company" />
                        {option.name}
                      </li>
                    )}
                    placeholder="Search companies..."
                    onInputChange={searchValue => setCompanySearch(searchValue)}
                    isClearable
                    getOptionKey={(option: CompanyListItem) => option.id.toString()}
                  />
                </div>
              )}
            </FormRow>
          </FormSection>

          {/* Conditional Fields for Custom Offers */}
          {!isCustomType && (
            <InfoMessage color="blue-light">
              Organization, community, and company offers: Name, branding, description, labels and link are
              automatically pulled, but they may still be overridden using the form.
            </InfoMessage>
          )}
          <FormSection>
            <FormRow>
              <FormikTextField formikProps={formik} name="name" label="Name" />
            </FormRow>
            <FormRow>
              <FormikTextField formikProps={formik} name="description" label="Description" multiline rows={3} />
            </FormRow>
            <FormRow>
              <FormikTextField formikProps={formik} name="linkText" label="Link Text" />
              <FormikTextField
                formikProps={formik}
                name="linkUrl"
                label="Link URL"
                helperText={getTemplateVariablesHelperText()}
              />
            </FormRow>

            <FormRow>
              <div>
                <Label>Logo Upload</Label>
                <ImageUploader
                  getUploadUrl={filename => backofficeApi.images.getUploadUrl('partner-offers', filename)}
                  onUploadSuccess={image => formik.setFieldValue('logoUrl', image.secure_url)}
                  onRemoveImage={() => formik.setFieldValue('logoUrl', '')}
                  currentImageUrl={formik.values.logoUrl}
                  aspectRatio={1}
                  width={140}
                  shape="round"
                  renderUploadButton={() => <></>}
                />
              </div>
              <div>
                <Label>Banner Upload</Label>
                <ImageUploader
                  width={400}
                  renderUploadButton={() => <></>}
                  getUploadUrl={filename => backofficeApi.images.getUploadUrl('partner-offers', filename)}
                  onUploadSuccess={image => formik.setFieldValue('bannerUrl', image.secure_url)}
                  onRemoveImage={() => formik.setFieldValue('bannerUrl', '')}
                  currentImageUrl={formik.values.bannerUrl}
                  aspectRatio={340 / 135}
                />
              </div>
            </FormRow>
          </FormSection>
          <FormSection>
            <FormRow>
              <div>
                <Label>Banner label</Label>
                <LabelEditor
                  label={{ name: formik.values.bannerLabelText || '', color: formik.values.bannerLabelColor }}
                  renderLabel={label => <RoundedChip label={label.name} color={label.color} />}
                  index={0}
                  onUpdate={(_, updates) => {
                    if (updates.name) formik.setFieldValue('bannerLabelText', updates.name);
                    if (updates.color) formik.setFieldValue('bannerLabelColor', updates.color);
                  }}
                  onRemove={() => {
                    formik.setFieldValue('bannerLabelText', '');
                    formik.setFieldValue('bannerLabelColor', bluePlanetTheme.bluePlanetPalette.purple.main);
                  }}
                />
              </div>
            </FormRow>
          </FormSection>
          <div className="u-content-spacing-bottom">
            <Label>Labels</Label>
            <EditLabels
              labels={labels}
              renderLabel={label => (
                <Chip
                  color="white"
                  label={label.name}
                  style={{ backgroundColor: label.color, color: contrastColorRGB(convertHexToRGB(label.color)) }}
                />
              )}
              onChange={setLabels}
            />
          </div>
          <div>
            <h3 className="text-h4 u-content-spacing-bottom">Audience & Targeting</h3>
            <FormRow>
              {/* <div style={{ minWidth: '200px' }}>
                <Label>Target Audience</Label>
                <MultiSelect
                  placeholder="Everyone"
                  values={formik.values.audience
                    .map(audience => audienceOptions.find(option => option.value === audience))
                    .filter(Boolean)}
                  onChange={(values: any[]) =>
                    formik.setFieldValue(
                      'audience',
                      values.map(v => v.value),
                    )
                  }
                  options={audienceOptions}
                  getOptionLabel={option => option.label}
                />
              </div> */}
              <div style={{ minWidth: '200px' }}>
                <Label>Target Countries</Label>
                <MultiSelect
                  placeholder="All countries"
                  values={formik.values.audienceCountries
                    .map(country => countryOptions.find(option => option.value === country))
                    .filter(Boolean)}
                  onChange={(values: any[]) =>
                    formik.setFieldValue(
                      'audienceCountries',
                      values.map((v: any) => v.value),
                    )
                  }
                  options={countryOptions}
                  getOptionLabel={option => option.label}
                />
              </div>
            </FormRow>
          </div>
          {/* Submit Button */}
          <ButtonList>
            <Button type="submit" kind="primary" isLoading={isLoading}>
              {offer ? 'Update offer' : 'Create offer'}
            </Button>
          </ButtonList>
        </FormGrid>
      </form>

      {/* Sticky Sidebar with Preview */}
      <SidebarContainer>
        <h4 className="text-h4 u-content-spacing-bottom">Preview</h4>
        <SelectedPartnerOffer offer={previewOffer} />
      </SidebarContainer>
    </MainContainer>
  );
}
